import React, { useState } from "react";
import { Mail, Phone, MapPin, ArrowRight, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import SEO from "@/components/SEO";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", inquiry_type: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", { ...form, subject: form.inquiry_type ? `[${form.inquiry_type}] ${form.subject}`.trim() : form.subject });
      setSent(true);
      toast.success("Message sent — we'll be in touch shortly.");
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Failed to send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="max-w-7xl mx-auto px-6 md:px-10 py-16">
      <SEO
        title="Contact — Get in touch"
        description="Talk to the Cake World USA wholesale team about your dessert program, private label or nationwide cold-chain delivery."
      />
      <div className="grid lg:grid-cols-12 gap-12">
        {/* Info */}
        <div className="lg:col-span-5">
          <div className="eyebrow">Contact Us</div>
          <h1 className="font-display text-5xl sm:text-6xl text-brand-ink mt-2 leading-none">We'd love to hear from you.</h1>
          <p className="mt-6 text-brand-stone leading-relaxed font-ui">
            We will be happy to assist you — leave us a message with your questions, feedback or something you think we can improve.
          </p>

          <ul className="mt-10 space-y-5 text-sm">
            <li className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-brand-pinkLight grid place-items-center text-brand-red"><Mail className="w-4 h-4" /></div>
              <div><div className="eyebrow">Email</div><div className="text-brand-ink mt-0.5 font-ui">info@cakeworldusa.com</div></div>
            </li>
            <li className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-brand-pinkLight grid place-items-center text-brand-red"><Phone className="w-4 h-4" /></div>
              <div><div className="eyebrow">Phone</div><div className="text-brand-ink mt-0.5 font-ui">+1 (623) 324-7530</div></div>
            </li>
            <li className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-brand-pinkLight grid place-items-center text-brand-red"><MapPin className="w-4 h-4" /></div>
              <div><div className="eyebrow">Location</div><div className="text-brand-ink mt-0.5 font-ui">Phoenix, AZ — nationwide shipping</div></div>
            </li>
          </ul>

          <div className="mt-10 rounded-3xl overflow-hidden border border-brand-line shadow-soft">
            <iframe
              title="Map"
              width="100%"
              height="260"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              src="https://www.google.com/maps?q=Phoenix,AZ&output=embed"
            />
          </div>
        </div>

        {/* Form */}
        <div className="lg:col-span-7">
          {sent ? (
            <div data-testid="contact-success" className="bg-white rounded-3xl border border-brand-line p-10 text-center shadow-warm">
              <div className="w-14 h-14 mx-auto rounded-full bg-brand-pink/60 grid place-items-center"><CheckCircle2 className="w-6 h-6 text-brand-ink" /></div>
              <h2 className="mt-6 font-display text-3xl text-brand-ink">Message sent</h2>
              <p className="mt-3 text-brand-stone">We received your note and will get back to you within one business day.</p>
              <button onClick={() => { setSent(false); setForm({ name:"", email:"", phone:"", subject:"", inquiry_type:"", message:"" }); }} className="mt-6 text-sm font-semibold text-brand-red">Send another message</button>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-white rounded-3xl border border-brand-line p-8 shadow-soft space-y-5" data-testid="contact-form" aria-label="contact form">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Name" required value={form.name} onChange={set("name")} testId="contact-name" />
                <Field label="Email" type="email" required value={form.email} onChange={set("email")} testId="contact-email" />
                <Field label="Phone" value={form.phone} onChange={set("phone")} testId="contact-phone" />
                <div>
                  <label className="text-xs font-semibold tracking-wide text-brand-ink uppercase font-ui">Inquiry type</label>
                  <select
                    value={form.inquiry_type}
                    onChange={set("inquiry_type")}
                    data-testid="contact-inquiry-type"
                    className="mt-1.5 w-full h-11 rounded-xl bg-white border border-brand-line px-3 text-sm font-ui focus:outline-none focus:ring-2 focus:ring-brand-red"
                  >
                    <option value="">Select the department…</option>
                    <option value="Hotel / Restaurant">I'm a hotel / restaurant</option>
                    <option value="Distributor">I'm a distributor</option>
                    <option value="Manufacturer">I'm a manufacturer</option>
                    <option value="General inquiry">General inquiry</option>
                  </select>
                </div>
              </div>
              <Field label="Subject" value={form.subject} onChange={set("subject")} testId="contact-subject" />
              <div>
                <label className="text-xs font-semibold tracking-wide text-brand-ink uppercase font-ui">Message <span className="text-brand-red">*</span></label>
                <textarea
                  value={form.message}
                  onChange={set("message")}
                  required
                  rows={6}
                  data-testid="contact-message"
                  className="mt-1.5 w-full rounded-xl bg-white border border-brand-line px-3 py-2.5 text-sm font-ui focus:outline-none focus:ring-2 focus:ring-brand-red resize-none"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-brand-red text-white text-sm font-ui font-semibold hover:bg-brand-redDark disabled:opacity-50 transition"
              >
                {submitting ? "Sending…" : "Send"} <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, value, onChange, type = "text", testId }) {
  return (
    <div>
      <label className="text-xs font-semibold tracking-wide text-brand-ink uppercase">
        {label} {required && <span className="text-brand-red">*</span>}
      </label>
      <input
        type={type} value={value} onChange={onChange} required={required}
        data-testid={testId}
        className="mt-1.5 w-full h-11 rounded-xl bg-white border border-brand-line px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red"
      />
    </div>
  );
}
